import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import ArrowDownIcon from '@/components/Icon/ArrowDownIcon';
import SingleArrowNext from '@/components/Icon/SingleArrowNext';
import UserIcon from '@/components/Icon/UserIcon';
import { menuList } from '@/constants/menu';
import { useModalStore } from '@/store/modalStore';
import { useTokenStore } from '@/store/tokenStore';

import * as S from './style';

interface MobileMenuProps {
	isOpen: boolean;
	onClickIsOpen: (isOpen: boolean) => void;
	onOpenMypage?: () => void;
}
interface SubMenu {
	path: string;
	menu: string;
}

const MobileMenu = ({ isOpen, onClickIsOpen, onOpenMypage }: MobileMenuProps) => {
	const { t } = useTranslation();
	const [openMenus, setOpenMenus] = useState<number[]>([]);
	const navigate = useNavigate();
	const { showModal } = useModalStore();
	const { isLogin } = useTokenStore();

	const toggleSubMenu = (index: number) => {
		openMenus.includes(index) ? setOpenMenus(openMenus.filter((item) => item !== index)) : setOpenMenus([...openMenus, index]);
	};
	const onClickCloseButton = () => {
		document.body.classList.remove('hidden');
		onClickIsOpen(false);
	};

	const onClickMenu = ({ path, menu }: SubMenu) => {
		navigate(path ?? '', { state: { menu: menu } });
		document.body.classList.remove('hidden');
		onClickIsOpen(false);
	};
	const onClickSubMenu = (path: string, title: string) => {
		if (title === '회원가입') {
			if (isLogin()) {
				onClickCloseButton();
				showModal(t('이미 한국응용과학기술학회 회원이십니다.'), 'confirm');
				return;
			} else {
				onClickMenu({ path: '/auth/signup', menu: title });
				return;
			}
		}

		if (title === '가입안내' || title === '연회비 납부') {
			if (!isLogin()) {
				onClickCloseButton();
				showModal(t('common.alerts.loginRequired'), 'login', t('common.actions.goToLogin'), () => navigate('/auth/login'));
				return;
			}
		}
		onClickMenu({ path, menu: title });
	};

	const handleLoginClick = () => {
		onClickMenu({ path: '/auth/login', menu: '로그인' });
	};

	const handleMypageClick = () => {
		onClickCloseButton();
		onOpenMypage?.();
	};

	return (
		<S.ModalOverlay isOpen={isOpen}>
			<S.ModalHeader>
				<S.CloseButton type="button" onClick={onClickCloseButton} aria-label="Close menu" />
			</S.ModalHeader>

			<S.ModalContent>
				<S.AuthBar>
					{isLogin() ? (
						<S.MypageButton type="button" onClick={handleMypageClick}>
							<UserIcon width="20" height="20" />
							MY
						</S.MypageButton>
					) : (
						<S.LoginButton type="button" onClick={handleLoginClick}>
							{t('로그인')}
						</S.LoginButton>
					)}
				</S.AuthBar>

				<S.MenuList>
					{menuList.map((menu, index) => {
						const hasSubMenu = (menu.subMenu?.length ?? 0) > 0;
						const isSubMenuOpen = openMenus.includes(index);

						return (
							<S.MenuItem key={menu.mainMenu}>
								{hasSubMenu ? (
									<S.MainMenuButton type="button" onClick={() => toggleSubMenu(index)} aria-expanded={isSubMenuOpen}>
										<S.MainMenuTrigger>
											<span>{t(menu.mainMenu)}</span>
											<S.MainMenuArrow $open={isSubMenuOpen}>
												<ArrowDownIcon />
											</S.MainMenuArrow>
										</S.MainMenuTrigger>
									</S.MainMenuButton>
								) : (
									<>
										{menu.mainMenuKey ? (
											<S.MainMenuButton
												type="button"
												onClick={() =>
													onClickMenu({
														path: menu.mainPath ?? '/',
														menu: menu.mainMenuKey ?? menu.mainMenu,
													})
												}
											>
												<S.MainMenuTrigger>
													<span>{t(menu.mainMenu)}</span>
												</S.MainMenuTrigger>
											</S.MainMenuButton>
										) : (
											<S.MainMenuLink to={menu.mainPath ?? '/'} onClick={onClickCloseButton}>
												{t(menu.mainMenu)}
											</S.MainMenuLink>
										)}
									</>
								)}
								{hasSubMenu && isSubMenuOpen && (
									<S.SubMenuList>
										{menu.subMenu?.map((subMenu, i) => (
											<S.SubMenuItem key={i}>
												<S.SubMenuButton
													type="button"
													onClick={() =>
														onClickSubMenu(subMenu.path ?? '', subMenu.menuKey ?? subMenu.title ?? '')
													}
												>
													<span>{subMenu.title ? t(subMenu.title) : ''}</span>
													<SingleArrowNext width="20" height="20" iconColor="#868E96" />
												</S.SubMenuButton>
											</S.SubMenuItem>
										))}
									</S.SubMenuList>
								)}
							</S.MenuItem>
						);
					})}
				</S.MenuList>
			</S.ModalContent>
		</S.ModalOverlay>
	);
};

export default MobileMenu;
